import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bed, Bath, Maximize, MapPin } from "lucide-react";
import { Link } from "react-router-dom";

const PropertyCard = ({ property, featured = false }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <Card 
      className={`group relative overflow-hidden bg-white border border-border/50 hover:shadow-xl transition-all duration-500 ${
        featured ? 'h-full' : ''
      }`}
      data-testid={`property-card-${property.mls_number}`}
    >
      <div className="relative overflow-hidden aspect-[4/3]">
        <img
          src={property.images[0]?.url || 'https://via.placeholder.com/800x600'}
          alt={`${property.address.street_number} ${property.address.street_name}`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute top-4 right-4 bg-accent text-accent-foreground px-4 py-2 text-sm font-semibold uppercase tracking-widest">
          {property.property_type}
        </div>
        <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-4 py-2">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">MLS #{property.mls_number}</p>
        </div>
      </div>

      <CardContent className="p-6">
        <div className="mb-4">
          <h3 className="text-2xl font-heading font-semibold text-primary mb-2" data-testid={`property-address-${property.mls_number}`}>
            {property.address.street_number} {property.address.street_name} {property.address.street_type}
          </h3>
          <p className="flex items-center text-sm text-muted-foreground">
            <MapPin className="w-4 h-4 mr-2" />
            {property.address.city}, {property.address.province}
          </p>
        </div>

        <div className="mb-6">
          <p className="text-3xl font-heading font-bold text-accent" data-testid={`property-price-${property.mls_number}`}>
            {formatPrice(property.price)}
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6 pb-6 border-b border-border">
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <Bed className="w-5 h-5 text-accent" />
            </div>
            <p className="text-lg font-semibold text-primary">{property.bedrooms}</p>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Beds</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <Bath className="w-5 h-5 text-accent" />
            </div>
            <p className="text-lg font-semibold text-primary">{property.bathrooms}</p>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Baths</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <Maximize className="w-5 h-5 text-accent" />
            </div>
            <p className="text-lg font-semibold text-primary">{property.square_feet || 'N/A'}</p>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Sq Ft</p>
          </div>
        </div>

        <Link to={`/property/${property.mls_number}`}>
          <Button 
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-none uppercase tracking-widest text-sm font-semibold transition-all duration-300"
            data-testid={`view-details-${property.mls_number}`}
          >
            View Details
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
};

export default PropertyCard;